import type { City, Webcam, Event } from "@/types";

export interface UserData {
    id: number;
    name: string;
    photo_url: string;
    ghost_mode_enabled: boolean;
}

export interface AppData {
    user_id: number;
    city_id: number;
    city_name?: string;
    city_tag: string;
    events: Event[];
    webcams: Webcam[];
    is_premium: boolean;
    token: string;
    centrifugo_connection_token: string;
    is_notifications_enabled: boolean;
    reports: number;
    is_event_reports_enabled: boolean;
    is_silent_mode_enabled: boolean;
    is_voice_reports_enabled: boolean;
    show_rating_modal: boolean;
    contest: any;
    _cities: City[];
    _ab_tests?: Array<{ tag: string, type: string }>;
    tariff?: {
        month: number;
        half_year: number;
        year: number;
    };
    user?: UserData;
}



export interface LocationClarificationRequest {
    message_id?: number;
    location_id?: number;
    longitude: number;
    latitude: number;
    comment: string;
}

export interface LocationClarificationData {
    id: number;
    user_id: number;
    message_id: number;
    longitude: number;
    latitude: number;
    comment: string;
    status: number;
    created_at: number;
}
export interface LocationClarificationResponse {
    data: LocationClarificationData;
}

export interface VoteRequest {
    message_id: number;
    vote_type: boolean; // true = like (актуально), false = dislike (неактуально)
}

export interface VoteResponse {
    success: boolean;
}

export interface VoteStatusResponse {
    data: {
        has_vote: boolean;
        vote_type: boolean;
        total_likes: number;
        total_dislikes: number;
    };
    success: boolean;
}

export interface CreateUserReportRequest {
    city_id: number;
    location_name?: string;
    comment?: string;
    latitude: number;
    longitude: number;
    message_type: 1 | 2 | 3; // 1 = DPS, 2 = Clean, 3 = DTP
}

export interface CreateUserReportResponse {
    success: boolean;
    data?: {
        id: number;
        created_at: number;
    };
}

const API_BASE_URL = 'https://dpscontrol.ru';
const initDataDev = import.meta.env.VITE_DEVELOP_MODE;
let urlInitData: string | null = null;

export const setUrlInitData = (data: string | null) => {
    urlInitData = data;
};

const getTelegramInitData = (): string => {
    // Если есть из URL - используем его
    if (urlInitData) {
        return urlInitData;
    }

    // Иначе берем из Telegram
    if (window.Telegram?.WebApp?.initData) {
        return window.Telegram.WebApp.initData;
    }

    // Для разработки
    if (process.env.NODE_ENV === 'development') {
        console.warn('Telegram WebApp не найден, используется тестовый initData');
        return initDataDev;
    }

    return '';
};

const apiRequest = async <T>(
    url: string,
    options: RequestInit = {}
): Promise<T> => {
    const initData = getTelegramInitData();

    // Добавляем базовый URL если путь относительный
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    console.log('🌐 API запрос:', fullUrl);
    console.log('🔐 Init Data:', initData ? 'установлен' : 'отсутствует');

    const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'X-Telegram-Init-Data': initData,
            ...options.headers,
        },
        ...options,
    });

    if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('📦 API ответ для', fullUrl, ':', data);

    return data;
};

export const apiClient = {

    getAppData: async (): Promise<AppData> => {
        const source = window.Telegram?.WebApp?.initDataUnsafe?.start_param;

        let url = `${API_BASE_URL}/webapp/data`;
        if (source) {
            url += `?start=${source}`;
        }
        return apiRequest<AppData>(url);
    },

    saveCity: async (cityId: number): Promise<{ success: boolean }> => {
        const initData = getTelegramInitData();

        console.log('💾 Сохранение города:', cityId);

        const response = await fetch(`${API_BASE_URL}/webapp/settings`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Telegram-Init-Data': initData,
            },
            body: JSON.stringify({
                key: 'city_id',
                value: cityId.toString(),
            }),
        });

        if (!response.ok) {
            throw new Error(`Save City Error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Город сохранен:', result);

        return result;
    },

    saveSettings: async (key: string, value: string): Promise<{ success: boolean }> => {
        const initData = getTelegramInitData();

        console.log('⚙️ Сохранение настройки:', key, '=', value);

        const response = await fetch(`${API_BASE_URL}/webapp/settings`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Telegram-Init-Data': initData,
            },
            body: JSON.stringify({
                key: key,
                value: value,
            }),
        });

        if (!response.ok) {
            throw new Error(`Save Settings Error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Настройка сохранена:', result);

        return result;
    },

    submitRating: async (rating: number, feedback: string): Promise<{ success: boolean }> => {
        const initData = getTelegramInitData();

        console.log('⭐ Отправка рейтинга:', rating, feedback);

        const response = await fetch(`${API_BASE_URL}/webapp/rating`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Telegram-Init-Data': initData,
            },
            body: JSON.stringify({
                rating: rating,
                feedback: feedback,
            }),
        });

        if (!response.ok) {
            throw new Error(`Submit Rating Error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Рейтинг отправлен:', result);

        return result;
    },
    async reportLocationClarification(data: LocationClarificationRequest): Promise<LocationClarificationResponse> {
        const initData = window.Telegram?.WebApp?.initData || import.meta.env.VITE_DEVELOP_MODE || '';

        console.log('📍 Отправляем уточнение локации:', data);

        try {
            const response = await fetch('https://dpscontrol.ru/webapp/report', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Telegram-Init-Data': initData
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('✅ Уточнение локации отправлено успешно:', result);
            console.log('🆔 Получен ID отчета:', result.data?.id);
            console.log('📊 Статус:', result.data?.status);

            return result;
        } catch (error) {
            console.error('❌ Ошибка отправки уточнения локации:', error);
            throw error;
        }
    },
    submitVote: async (data: VoteRequest): Promise<VoteResponse> => {
        const initData = getTelegramInitData();

        console.log('🗳️ Отправляем голос:', data);
        console.log('👍 Тип голоса:', data.vote_type ? 'актуально (like)' : 'неактуально (dislike)');

        try {
            const response = await fetch(`${API_BASE_URL}/webapp/vote`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Telegram-Init-Data': initData
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('✅ Голос отправлен успешно:', result);

            return result;
        } catch (error) {
            console.error('❌ Ошибка отправки голоса:', error);
            throw error;
        }
    },

    // Получение статуса голосования
    getVoteStatus: async (messageId: number): Promise<VoteStatusResponse> => {
        console.log('📊 Получаем статус голосования для сообщения:', messageId);

        try {
            const response = await apiRequest<VoteStatusResponse>(
                `/webapp/vote?message_id=${messageId}`
            );

            console.log('📈 Статус голосования получен:', response);
            console.log('🗳️ Проголосовал ли пользователь:', response.data?.has_vote);
            console.log('👍 Лайков:', response.data?.total_likes);
            console.log('👎 Дизлайков:', response.data?.total_dislikes);

            return response;
        } catch (error) {
            console.error('❌ Ошибка получения статуса голосования:', error);
            throw error;
        }
    },

    submitLocationVote: async (data: { location_id: number, vote_type: boolean }): Promise<VoteResponse> => {
        const initData = getTelegramInitData();

        try {
            const response = await fetch(`${API_BASE_URL}/webapp/vote`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Telegram-Init-Data': initData
                },
                body: JSON.stringify({
                    message_id: 0,
                    location_id: data.location_id,
                    vote_type: data.vote_type
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Ошибка голосования за локацию:', error);
            throw error;
        }
    },

    getLocationVoteStatus: async (locationId: number): Promise<VoteStatusResponse> => {
        try {
            return await apiRequest<VoteStatusResponse>(
                `/webapp/vote?location_id=${locationId}`
            );
        } catch (error) {
            console.error('Ошибка получения статуса голосования за локацию:', error);
            throw error;
        }
    },
    createUserReport: async (data: CreateUserReportRequest): Promise<CreateUserReportResponse> => {
        const initData = getTelegramInitData();

        console.log('🆕 Создание пользовательского репорта:', data);

        try {
            const response = await fetch(`${API_BASE_URL}/webapp/user-report`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Telegram-Init-Data': initData
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('✅ Репорт создан успешно:', result);

            return result;
        } catch (error) {
            console.error('❌ Ошибка создания репорта:', error);
            throw error;
        }
    },
    logLocation: async (latitude: number, longitude: number): Promise<{ success: boolean }> => {
        const initData = getTelegramInitData();

        try {
            const response = await fetch(`${API_BASE_URL}/webapp/location/log`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Telegram-Init-Data': initData,
                },
                body: JSON.stringify({
                    latitude,
                    longitude,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.warn('⚠️ Ошибка логирования координат:', error);
            return { success: false };
        }
    },
};

export const logError = async (errorData: any): Promise<void> => {
    const initData = getTelegramInitData();

    try {
        await fetch(`${API_BASE_URL}/log-error`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Telegram-Init-Data': initData,
            },
            body: JSON.stringify({
                timestamp: Date.now(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                initData: initData,
                ...errorData,
            }),
        });
    } catch (error) {
        console.error('Ошибка отправки лога:', error);
    }
};